/* --------------------------------------------------------------------------
   useForecastFilters.js – small reactive store that centralises the Operator &
   Chemical selections across the Inventory-Forecasting views.
--------------------------------------------------------------------------- */

import { reactive, computed, ref, watch } from 'vue';
import store from '@/store';
import { getEnv } from '@/env-runtime';

// --- Operator endpoint lives in the main R3 backend -----------------------
// We reuse the Vuex store helper so we automatically get the correct API base
// URL (config module) and the auth token (auth module). No Function-key logic
// required here.

// --- Forecast service (chemicals) sits in Azure Functions ------------------
// Resolve Functions API base - same priority order as useForecastApi.js
let FN_API_BASE = (
  getEnv('VITE_FUNCTIONS_BASE') ||
  getEnv('VUE_APP_FUNCTIONS_BASE') ||
  getEnv('FUNCTIONS_BASE') ||
  ''
).replace(/\/$/, '');
if (!FN_API_BASE) {
  console.warn('[useForecastFilters] FUNCTIONS_BASE env missing; defaulting to local proxy (/api).');
  FN_API_BASE = '/api';
}

const FUNC_KEY = (
  getEnv('VITE_FUNCTIONS_KEY') ||
  getEnv('VUE_APP_FUNCTIONS_KEY') ||
  getEnv('VITE_AZ_FN_KEY') ||
  getEnv('VUE_APP_AZ_FN_KEY') ||
  getEnv('FUNCTIONS_KEY') ||
  ''
).trim();

function buildFnHeaders() {
  return {
    ...(FUNC_KEY ? { 'x-functions-key': FUNC_KEY } : {}),
    Accept: 'application/json',
  };
}

function withCodeParam(path) {
  return FUNC_KEY ? `${path}${path.includes('?') ? '&' : '?'}code=${encodeURIComponent(FUNC_KEY)}` : path;
}

// ---------------------------------------------------------------------
// 1️⃣  Reactive selection state (the part other components care about)
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// 1️⃣  Reactive selection state (the part other components care about)
// ---------------------------------------------------------------------

const state = reactive({
  operatorId: null,   // number | null
  chemical: null,   // string | null

  // Scope selection (optional)
  // type: 'operator' (default) | 'area' | 'office' | 'district'
  // id:   number
  selectedScope: { type: 'operator', id: null },
});

// ---------------------------------------------------------------------
// 2️⃣  Operator lookup list (fetched once, cached for session)
// ---------------------------------------------------------------------

const operatorList = ref([]);   // [{ operatorId, operatorName }, …]
const operatorLoadError = ref(null);

async function fetchOperators() {
  try {
    // 1) check if operators already loaded in store
    const cached = store.getters['operators/getOperators'];
    if (cached && cached.length) {
      operatorList.value = cached;
      return;
    }

    // 2) dispatch store action – this handles token/auth & API base behind the scenes
    await store.dispatch('operators/loadOperators');

    operatorList.value = store.getters['operators/getOperators'];
  } catch (err) {
    operatorLoadError.value = err?.message ?? String(err);
    console.error('[useForecastFilters] fetchOperators() failed:', err);
  }
}

// Not at import: the router imports the forecasting page eagerly, so this ran
// on every app load, login screen included, before there was a token.
// ControlPanel loads operators on mount through ensureOperatorsLoaded().
// if (operatorList.value.length === 0) {
//   fetchOperators();
// }

// Helper for views/components to guarantee operators are loaded on mount
export async function ensureOperatorsLoaded() {
  if (!operatorList.value || operatorList.value.length === 0) {
    await fetchOperators();
  }
  return operatorList.value;
}

// Expose operators as computed for reactivity
export const operators = computed(() => operatorList.value);

// ---------------------------------------------------------------------
// 3️⃣  Chemicals & Scopes – fetched whenever operatorId changes
// ---------------------------------------------------------------------

const chemicalList = ref([]);   // ["Methanol", …]
const chemicalsLoading = ref(false);
const chemicalsError = ref(null);

// Scope lookup lists
const districtsList = ref([]);
const officesList = ref([]);
const areasList = ref([]);
const lookupsLoading = ref(false);

async function fetchChemicals(operatorId) {
  if (!operatorId) return;
  chemicalsLoading.value = true;
  chemicalsError.value = null;
  try {
    const url = withCodeParam(`${FN_API_BASE}/r3moteio_ai/lookup/chemicals?operatorId=${operatorId}`);
    const resp = await fetch(url, {
      headers: buildFnHeaders(),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    chemicalList.value = await resp.json();
  } catch (err) {
    chemicalsError.value = err?.message ?? String(err);
    console.error('[useForecastFilters] fetchChemicals() failed:', err);
    chemicalList.value = [];
  } finally {
    chemicalsLoading.value = false;
  }
}

async function fetchLookups(operatorId) {
  if (!operatorId) return;
  lookupsLoading.value = true;

  // Parallel fetch for all scope types
  const endpoints = ['districts', 'offices', 'areas'];
  const chemParam = state.chemical ? `&chemical=${encodeURIComponent(state.chemical)}` : '';

  try {
    const results = await Promise.all(
      endpoints.map(async (ep) => {
        const url = withCodeParam(`${FN_API_BASE}/r3moteio_ai/lookup/${ep}?operatorId=${operatorId}${chemParam}`);
        const resp = await fetch(url, { headers: buildFnHeaders() });
        if (!resp.ok) throw new Error(`HTTP ${resp.status} for ${ep}`);
        return resp.json();
      })
    );

    districtsList.value = results[0];
    officesList.value = results[1];
    areasList.value = results[2];

  } catch (err) {
    console.error('[useForecastFilters] fetchLookups() failed:', err);
    // Reset on error
    districtsList.value = [];
    officesList.value = [];
    areasList.value = [];
  } finally {
    lookupsLoading.value = false;
  }
}

// Automatically fetch when operatorId or chemical changes
watch(
  [() => state.operatorId, () => state.chemical],
  ([newOpId, newChem], [oldOpId, oldChem]) => {
    // If operator changed, reset everything
    if (newOpId !== oldOpId) {
      chemicalList.value = [];
      districtsList.value = [];
      officesList.value = [];
      areasList.value = [];

      state.chemical = null;
      state.selectedScope = { type: 'operator', id: null };

      if (newOpId) {
        fetchChemicals(newOpId);
        fetchLookups(newOpId);
      }
    }
    // If only chemical changed, re-fetch lookups (scopes might narrow)
    else if (newOpId && newChem !== oldChem) {
      // Reset scope selection as it might be invalid for new chemical
      state.selectedScope = { type: 'operator', id: null };
      fetchLookups(newOpId);
    }
  },
  { immediate: false }
);

export const chemicals = computed(() => {
  const allChems = chemicalList.value || [];
  const { type, id } = state.selectedScope;

  if (!id || type === 'operator') {
    return allChems;
  }

  // Find the selected scope item (district/office/area)
  let scopeItem = null;
  if (type === 'district') {
    scopeItem = districtsList.value.find(d => d.id === id);
  } else if (type === 'office') {
    scopeItem = officesList.value.find(o => o.id === id);
  } else if (type === 'area') {
    scopeItem = areasList.value.find(a => a.id === id);
  }

  // If scope has specific chemicals, filter the main list
  if (scopeItem && Array.isArray(scopeItem.chemicals)) {
    const allowed = new Set(scopeItem.chemicals.map(c => c.toLowerCase()));
    return allChems.filter(c => allowed.has(c.toLowerCase()));
  }

  return allChems;
});
export const districts = computed(() => districtsList.value);
export const offices = computed(() => officesList.value);
export const areas = computed(() => areasList.value);

// ---------------------------------------------------------------------
// 4️⃣  Mutator helpers (consumed by ControlPanel & ScopeFilters)
// ---------------------------------------------------------------------

export function setOperator(id) {
  state.operatorId = id;
  // Reset downstream selection to enforce cascading UX
  state.chemical = null;
  state.selectedScope = { type: 'operator', id: null };
}

export function setChemical(val) {
  if (val && typeof val === 'object') {
    // Safeguard: if dropdown emits object, try to grab ID or name
    state.chemical = val.id || val.name || String(val);
  } else {
    state.chemical = val;
  }
}

export function setScope(type, id) {
  // type: 'operator' | 'area' | 'office' | 'district'
  // id: number | null
  state.selectedScope = { type, id };
}

// ---------------------------------------------------------------------
// 5️⃣  Default export (the reactive state object itself)
// ---------------------------------------------------------------------

export default state;
